import type { Presentation, PresentationV2 } from '../presentation/types.ts';
import { chooseArtImage, fetchArtRelease, ART_HASH } from './release.ts';
import { validateAtlases } from '../faces/validate.ts';

export function artAsset(p: Presentation | PresentationV2, id: string, width = 240, dpr = typeof window === 'undefined' ? 1 : window.devicePixelRatio) {
  const hosted = 'format' in p ? p.artRelease?.assets.find(a => a.id === id) : undefined;
  const image = hosted ? chooseArtImage(hosted.images, width, dpr) : undefined;
  if (image) return { url: `/assets/${image.sha256}`, width: image.width, height: image.height };
  const local = p.manifest.assets.find(a => a.id === id), url = p.assetUrls.get(id);
  return local && url ? { url, width: local.width, height: local.height } : null;
}

export async function withArtRelease(p: PresentationV2, hash = p.manifest.artRelease?.sha256): Promise<PresentationV2> {
  const ref = p.manifest.artRelease; if (!ref || !hash) return p;
  const release = await fetchArtRelease({ ...ref, sha256: hash });
  const local = new Set(p.manifest.assets.map(a => a.id));
  if (release.assets.some(a => local.has(a.id))) throw new Error('Hosted and bundled artwork IDs overlap.');
  const sheets = new Map(p.manifest.assets.map(a => [a.id, { width: a.width, height: a.height }]));
  for (const a of release.assets) { const image = a.images.at(-1)!; sheets.set(a.id, { width: image.width, height: image.height }); }
  if (p.atlases && validateAtlases(p.atlases, sheets).some(i => i.severity === 'error')) throw new Error('Artwork atlas does not match its frames.');
  const assetUrls = new Map(p.manifest.assets.map(a => [a.id, `/assets/${a.sha256}`]));
  for (const a of release.assets) assetUrls.set(a.id, `/assets/${chooseArtImage(a.images, 240)!.sha256}`);
  return { ...p, artRelease: release, artHash: hash, assetUrls, artNotice: undefined };
}

/** Explicit refresh (or opt-in authoring watch); never changes the game/session state. */
export async function refreshArt(p: PresentationV2): Promise<PresentationV2> {
  const ref = p.manifest.artRelease; if (!ref) return p;
  const response = await fetch(`/art/channels/${ref.id}`, { cache: 'no-store', signal: AbortSignal.timeout(10000) });
  if (!response.ok) throw new Error('No published artwork update is available.');
  const text = await response.text(); if (text.length > 1024) throw new Error('Invalid art channel.');
  const current = JSON.parse(text) as { id: string; sha256: string };
  if (current.id !== ref.id || !ART_HASH.test(current.sha256)) throw new Error('Invalid art channel.');
  return current.sha256 === p.artHash ? p : withArtRelease(p, current.sha256);
}
